import { ART_LABELS_URL, CARDS_URL } from "../../context/paths";

const headers = (token) => ({
    "Content-Type": "application/json",
    "authorization": `Bearer ${token}`,
});

const request = async (url, options) => {
    try {
        const response = await fetch(url, options);
        return await response.json();
    } catch (error) {
        return { message: error.message, type: "error" };
    }
};

const withQuery = (base, params) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") search.append(key, String(value));
    });
    const qs = search.toString();
    return qs ? `${base}?${qs}` : base;
};

export const artLabelUrl = (artKey) => `${ART_LABELS_URL}/${encodeURIComponent(artKey)}`;

export const searchCards = (token, { q, lang = "pt", set } = {}) =>
    request(withQuery(`${CARDS_URL}/search`, { q, lang, set }), { method: "GET", headers: headers(token) });

/** Campos do modelo no padrão dos modelos existentes (spec 2026-10-01 §5); 502 CARD_SEARCH_UNAVAILABLE se o TCGdex cair. */
export const getCardModelFields = (token, cardId, lang) =>
    request(withQuery(`${CARDS_URL}/tcgdex/${encodeURIComponent(cardId)}/model-fields`, { lang }), { method: "GET", headers: headers(token) });

export const getArtLabelGeometry = (token) =>
    request(`${ART_LABELS_URL}/geometry`, { method: "GET", headers: headers(token) });

export const getArtLabels = (token, { status, set, q, page = 1, limit = 50 } = {}) =>
    request(withQuery(ART_LABELS_URL, { status, set, q, page, limit }), { method: "GET", headers: headers(token) });

export const getArtLabel = (token, artKey) =>
    request(artLabelUrl(artKey), { method: "GET", headers: headers(token) });

export const patchArtLabel = (token, artKey, body) =>
    request(artLabelUrl(artKey), { method: "PATCH", headers: headers(token), body: JSON.stringify(body) });

/**
 * `promptOverride`/`negativePromptOverride` substituem o padrão do método (spec prompt editável E1/E2);
 * `seed` vazia = aleatória. Campos ausentes não vão no corpo; `prompt` (complemento curto) segue aceito.
 */
export const generateArtLabel = (token, artKey, { prompt, numImages, methods, promptOverride, negativePromptOverride, seed } = {}) =>
    request(`${artLabelUrl(artKey)}/generate`, {
        method: "POST",
        headers: headers(token),
        body: JSON.stringify({
            prompt, numImages,
            ...(methods ? { methods } : {}),
            ...(promptOverride !== undefined ? { promptOverride } : {}),
            ...(negativePromptOverride !== undefined ? { negativePromptOverride } : {}),
            ...(seed !== undefined && seed !== null ? { seed } : {}),
        }),
    });

/** Prompt e negativo padrão por método, com os providers que de fato os usam. */
export const getArtLabelPromptDefaults = (token) =>
    request(`${ART_LABELS_URL}/prompt-defaults`, { method: "GET", headers: headers(token) });

export const reconcileArtLabel = (token, artKey) =>
    request(`${artLabelUrl(artKey)}/reconcile`, { method: "POST", headers: headers(token) });

export const approveArtLabel = (token, artKey, body) =>
    request(`${artLabelUrl(artKey)}/approve`, { method: "POST", headers: headers(token), body: JSON.stringify(body) });

export const rejectArtLabel = (token, artKey, note) =>
    request(`${artLabelUrl(artKey)}/reject`, { method: "POST", headers: headers(token), body: JSON.stringify({ note }) });

export const getArtLabelUploadUrl = (token, artKey) =>
    request(`${artLabelUrl(artKey)}/upload-url`, { method: "POST", headers: headers(token), body: JSON.stringify({ contentType: "image/png" }) });

/** Arte corrigida fora do sistema: a chave do upload-url vira uma variação nova (spec 2026-10-01 §6). */
export const postArtLabelCorrection = (token, artKey, s3Key) =>
    request(`${artLabelUrl(artKey)}/corrections`, { method: "POST", headers: headers(token), body: JSON.stringify({ s3Key }) });

export const getArtLabelByModel = (token, modelId) =>
    request(`${ART_LABELS_URL}/by-model/${encodeURIComponent(modelId)}`, { method: "GET", headers: headers(token) });

export const getArtLabelSuggestions = (token, modelId) =>
    request(withQuery(`${ART_LABELS_URL}/suggestions`, { modelId }), { method: "GET", headers: headers(token) });

export const createArtLabelUpload = (token, contentType) =>
    request(`${ART_LABELS_URL}/uploads`, { method: "POST", headers: headers(token), body: JSON.stringify({ contentType }) });

export const linkArtLabel = (token, body) =>
    request(`${ART_LABELS_URL}/link`, { method: "POST", headers: headers(token), body: JSON.stringify(body) });

export const getArtLabelsQueue = (token) =>
    request(`${ART_LABELS_URL}/queue`, { method: "GET", headers: headers(token) });
